<template>
  <nav class="left-rail" :class="{ 'left-rail--collapsed': collapsed }">
    <!-- Logo / Wordmark -->
    <RouterLink to="/" class="left-rail__logo" :title="collapsed ? 'INNEREALITY' : undefined">
      <svg class="left-rail__brand-arrow" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M7 17 L17 7" />
        <path d="M10 7 L17 7 L17 14" />
      </svg>
      <span class="left-rail__wordmark" v-if="!collapsed">
        <span class="left-rail__wordmark-inne">INNE</span><span class="left-rail__wordmark-reality">REALITY</span>
      </span>
    </RouterLink>

    <!-- Workspace tile (clickable — opens switcher dropdown) -->
    <button
      type="button"
      class="left-rail__workspace"
      :class="{ 'left-rail__workspace--open': workspaceMenuOpen }"
      :title="collapsed ? (workspaceLabel || 'Workspace') : 'Switch workspace'"
      :aria-expanded="workspaceMenuOpen"
      @click.stop="toggleWorkspaceMenu"
    >
      <span class="left-rail__workspace-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <rect x="3" y="3" width="8" height="8" rx="1.5"/>
          <rect x="13" y="3" width="8" height="8" rx="1.5"/>
          <rect x="3" y="13" width="8" height="8" rx="1.5"/>
          <rect x="13" y="13" width="8" height="8" rx="1.5"/>
        </svg>
      </span>
      <span class="left-rail__workspace-text" v-if="!collapsed">
        <span class="left-rail__workspace-label">{{ workspaceLabel || 'Personal workspace' }}</span>
        <span class="left-rail__workspace-sub" v-if="tenantLabel">{{ tenantLabel }}</span>
      </span>
      <span class="left-rail__chevron-down" v-if="!collapsed" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M6 9 L12 15 L18 9" />
        </svg>
      </span>
    </button>

    <!-- Workspace switcher dropdown -->
    <div v-if="workspaceMenuOpen" class="left-rail__workspace-menu" @click.stop>
      <div class="left-rail__workspace-menu-header">Switch workspace</div>
      <ul class="left-rail__workspace-menu-list">
        <li v-for="m in authStore.memberships" :key="m.kind + ':' + m.entityId">
          <button
            type="button"
            class="left-rail__workspace-menu-item"
            :class="{ 'left-rail__workspace-menu-item--active': m.isActive }"
            :disabled="m.isActive || authStore.isSwitchingContext"
            @click="onWorkspacePick(m)"
          >
            <span class="left-rail__workspace-menu-kind">{{ m.kind === 'org' ? 'Org' : 'Tenant' }}</span>
            <span class="left-rail__workspace-menu-name">{{ m.name || m.entityId }}</span>
            <span v-if="m.isActive" class="left-rail__workspace-menu-active-dot" aria-hidden="true"></span>
          </button>
        </li>
      </ul>
      <RouterLink to="/profile" class="left-rail__workspace-menu-footer" @click="workspaceMenuOpen = false">
        Manage workspaces
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M5 12 L19 12" />
          <path d="M13 6 L19 12 L13 18" />
        </svg>
      </RouterLink>
    </div>

    <!-- New campaign CTA -->
    <RouterLink to="/" class="left-rail__new-campaign" :title="collapsed ? 'New campaign' : undefined">
      <svg class="left-rail__new-campaign-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true">
        <path d="M12 5 L12 19" />
        <path d="M5 12 L19 12" />
      </svg>
      <span v-if="!collapsed" class="left-rail__new-campaign-label">New campaign</span>
    </RouterLink>

    <!-- Workspace section -->
    <div class="left-rail__section-label" v-if="!collapsed">Workspace</div>
    <div class="left-rail__divider" v-if="collapsed"></div>

    <RouterLink
      to="/dashboard"
      class="left-rail__nav-item"
      :class="{ 'left-rail__nav-item--active': isDashboardActive }"
      title="Dashboard"
    >
      <span class="left-rail__nav-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round">
          <rect x="3" y="3" width="8" height="10" rx="1.6"/>
          <rect x="13" y="3" width="8" height="6" rx="1.6"/>
          <rect x="13" y="11" width="8" height="10" rx="1.6"/>
          <rect x="3" y="15" width="8" height="6" rx="1.6"/>
        </svg>
      </span>
      <span class="left-rail__nav-label" v-if="!collapsed">Dashboard</span>
    </RouterLink>

    <RouterLink
      to="/dashboard/experiences"
      class="left-rail__nav-item"
      :class="{ 'left-rail__nav-item--active': isExperiencesActive }"
      title="Experience Library"
    >
      <span class="left-rail__nav-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round">
          <rect x="6" y="3" width="14" height="14" rx="2.2"/>
          <rect x="3" y="6" width="14" height="14" rx="2.2"/>
        </svg>
      </span>
      <span class="left-rail__nav-label" v-if="!collapsed">Experience Library</span>
    </RouterLink>

    <RouterLink
      to="/dashboard/analytics"
      class="left-rail__nav-item"
      :class="{ 'left-rail__nav-item--active': isAnalyticsActive }"
      title="Analytics (Beta)"
    >
      <span class="left-rail__nav-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 19 L5 13" />
          <path d="M10 19 L10 7" />
          <path d="M15 19 L15 14" />
          <path d="M20 19 L20 4" />
          <path d="M3 21 L21 21" />
        </svg>
      </span>
      <span class="left-rail__nav-label" v-if="!collapsed"><span class="left-rail__beta-tag">Beta</span>Analytics</span>
    </RouterLink>

    <!-- Account section -->
    <div class="left-rail__section-label left-rail__section-label--account" v-if="!collapsed">Account</div>
    <div class="left-rail__divider left-rail__divider--account" v-if="collapsed"></div>

    <RouterLink
      to="/billing"
      class="left-rail__nav-item"
      :class="{ 'left-rail__nav-item--active': isBillingActive }"
      title="Billing"
    >
      <span class="left-rail__nav-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round">
          <rect x="3" y="6" width="18" height="13" rx="2"/>
          <path d="M3 10 L21 10" />
          <path d="M7 15 L11 15" />
        </svg>
      </span>
      <span class="left-rail__nav-label" v-if="!collapsed">Billing</span>
    </RouterLink>

    <RouterLink
      to="/profile"
      class="left-rail__nav-item"
      :class="{ 'left-rail__nav-item--active': isProfileActive }"
      title="Account"
    >
      <span class="left-rail__nav-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="8" r="3.6"/>
          <path d="M5 20 C5 15.5 8.2 13.5 12 13.5 C15.8 13.5 19 15.5 19 20" />
        </svg>
      </span>
      <span class="left-rail__nav-label" v-if="!collapsed">Account</span>
    </RouterLink>

    <!-- Spacer -->
    <div class="left-rail__spacer"></div>

    <!-- Identity tile -->
    <div class="left-rail__identity" @click="toggleIdentityMenu" :title="collapsed ? (userName || 'Account') : undefined">
      <div class="left-rail__avatar">{{ avatarInitial }}</div>
      <div class="left-rail__identity-info" v-if="!collapsed">
        <span class="left-rail__identity-name">{{ userName || 'Account' }}</span>
      </div>
      <span class="left-rail__chevron-down" v-if="!collapsed" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M6 9 L12 15 L18 9" />
        </svg>
      </span>
    </div>

    <!-- Identity dropdown -->
    <div class="left-rail__identity-menu" v-if="identityMenuOpen">
      <button class="left-rail__identity-menu-item left-rail__identity-menu-item--danger" @click="handleSignOut">
        Sign out
      </button>
    </div>

    <!-- Collapse toggle -->
    <button
      class="left-rail__collapse-btn"
      @click="$emit('toggle')"
      :title="collapsed ? 'Expand sidebar ([)' : 'Collapse sidebar ([)'"
      :aria-label="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
    >
      <svg
        class="left-rail__collapse-icon"
        :class="{ 'left-rail__collapse-icon--collapsed': collapsed }"
        viewBox="0 0 24 24" width="14" height="14"
        fill="none" stroke="currentColor"
        stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M14 6 L8 12 L14 18" />
      </svg>
    </button>
  </nav>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { RouterLink } from 'vue-router'
import { useAuthStore } from '@/store/auth.store'
import type { Membership } from '@/services/profile'

export default defineComponent({
  name: 'LeftRail',
  components: { RouterLink },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle'],
  setup(_, { emit }) {
    const route = useRoute()
    const authStore = useAuthStore()
    const identityMenuOpen = ref(false)
    const workspaceMenuOpen = ref(false)

    const workspaceLabel = computed(() => authStore.activeOrgName)
    const tenantLabel = computed(() => authStore.activeTenantName)
    const userName = computed(() => {
      const { name, surname } = authStore.userDetails
      if (name && surname) return `${name} ${surname}`
      return name || authStore.userDetails.email || ''
    })
    const avatarInitial = computed(() => {
      const n = authStore.userDetails.name || authStore.userDetails.email || '?'
      return n.charAt(0).toUpperCase()
    })

    const isDashboardActive = computed(
      () => route.path === '/dashboard' || route.path.startsWith('/dashboard/campaigns')
    )
    const isExperiencesActive = computed(() => route.path.startsWith('/dashboard/experiences'))
    const isAnalyticsActive = computed(() => route.path.startsWith('/dashboard/analytics'))
    const isBillingActive = computed(() => route.path.startsWith('/billing'))
    const isProfileActive = computed(() => route.path.startsWith('/profile'))

    const toggleIdentityMenu = () => {
      identityMenuOpen.value = !identityMenuOpen.value
      if (workspaceMenuOpen.value) workspaceMenuOpen.value = false
    }

    const toggleWorkspaceMenu = () => {
      workspaceMenuOpen.value = !workspaceMenuOpen.value
      if (identityMenuOpen.value) identityMenuOpen.value = false
    }

    const onWorkspacePick = async (m: Membership) => {
      if (m.isActive) return
      workspaceMenuOpen.value = false
      try {
        if (m.kind === 'org') {
          await authStore.switchActiveContext({ orgId: m.entityId })
        } else {
          await authStore.switchActiveContext({ tenantId: m.entityId })
        }
        await authStore.loadMemberships()
      } catch (e) {
        console.error('Workspace switch failed', e)
      }
    }

    const handleSignOut = async () => {
      identityMenuOpen.value = false
      await authStore.logout()
    }

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.left-rail__identity') && !target.closest('.left-rail__identity-menu')) {
        identityMenuOpen.value = false
      }
      if (!target.closest('.left-rail__workspace') && !target.closest('.left-rail__workspace-menu')) {
        workspaceMenuOpen.value = false
      }
    }

    const handleKeydown = (e: KeyboardEvent) => {
      if (
        e.key === '[' &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        emit('toggle')
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
      window.addEventListener('keydown', handleKeydown)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('keydown', handleKeydown)
    })

    return {
      authStore,
      workspaceLabel,
      tenantLabel,
      userName,
      avatarInitial,
      identityMenuOpen,
      workspaceMenuOpen,
      isDashboardActive,
      isExperiencesActive,
      isAnalyticsActive,
      isBillingActive,
      isProfileActive,
      toggleIdentityMenu,
      toggleWorkspaceMenu,
      onWorkspacePick,
      handleSignOut,
    }
  },
})
</script>

<style scoped>
.left-rail {
  width: 264px;
  min-width: 264px;
  height: 100vh;
  background: #2c3e50;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 16px 12px;
  overflow: hidden;
  position: relative;
  transition: width 0.22s ease, min-width 0.22s ease;
  font-family: var(--font-sans);
  box-sizing: border-box;
}

.left-rail--collapsed {
  width: 64px;
  min-width: 64px;
  padding: 16px 8px;
  align-items: center;
}

/* Logo */
.left-rail__logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  padding: 10px 8px 18px 8px;
  color: #ffffff;
  flex-shrink: 0;
}

.left-rail__brand-arrow {
  color: #96ffc9;
  flex-shrink: 0;
  display: block;
}

.left-rail__wordmark {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  white-space: nowrap;
  color: #ffffff;
}

.left-rail__wordmark-inne {
  font-style: normal;
  font-weight: 700;
}

.left-rail__wordmark-reality {
  font-style: italic;
  font-weight: 700;
}

/* Workspace tile (clickable button) */
.left-rail__workspace {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px !important;
  border-radius: 12px !important;
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid transparent !important;
  margin-bottom: 12px;
  cursor: pointer;
  flex-shrink: 0;
  min-width: 0;
  width: 100%;
  text-align: left;
  font-family: var(--font-sans);
  color: inherit;
  transition: background 0.12s ease, border-color 0.12s ease;
}

.left-rail__workspace:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.left-rail__workspace--open {
  background: rgba(150, 255, 201, 0.1) !important;
  border-color: rgba(150, 255, 201, 0.25) !important;
}

.left-rail--collapsed .left-rail__workspace {
  justify-content: center;
  padding: 10px;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
}

.left-rail__workspace-icon {
  color: #96ffc9;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.left-rail__workspace-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.left-rail__workspace-label {
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.left-rail__workspace-sub {
  font-size: 11px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.left-rail__chevron-down {
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* New campaign CTA */
.left-rail__new-campaign {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: #96ffc9;
  color: #181b2f;
  border-radius: 29px;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 20px;
  transition: background 0.15s ease;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
}

.left-rail--collapsed .left-rail__new-campaign {
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 12px;
}

.left-rail__new-campaign:hover {
  background: #82e8b0;
}

.left-rail__new-campaign-icon {
  display: block;
  flex-shrink: 0;
  color: #181b2f;
}

/* Section labels */
.left-rail__section-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  padding: 0 8px;
  margin-bottom: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.left-rail__section-label--account {
  margin-top: 16px;
}

/* Dividers (collapsed state) */
.left-rail__divider {
  width: 32px;
  height: 1px;
  background: rgba(255, 255, 255, 0.12);
  margin: 8px auto;
  flex-shrink: 0;
}

.left-rail__divider--account {
  margin-top: 16px;
}

/* Nav items */
.left-rail__nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px;
  border-radius: 12px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 300;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  width: 100%;
  text-align: left;
  white-space: nowrap;
  flex-shrink: 0;
  box-sizing: border-box;
}

.left-rail--collapsed .left-rail__nav-item {
  justify-content: center;
  padding: 10px;
  width: 40px;
  height: 40px;
}

.left-rail__nav-item:hover {
  background: rgba(255, 255, 255, 0.06);
}

.left-rail__nav-item--active {
  background: #96ffc9 !important;
  color: #181b2f !important;
  font-weight: 700;
  border-radius: 29px;
}

.left-rail__nav-item--active .left-rail__nav-icon {
  color: #181b2f;
}

.left-rail__nav-item--disabled {
  opacity: 0.38;
  cursor: not-allowed;
  font-family: var(--font-sans);
}

.left-rail__nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.78);
  width: 20px;
  height: 20px;
}

.left-rail__nav-item--active .left-rail__nav-icon {
  color: #181b2f;
}

.left-rail__nav-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Spacer */
.left-rail__spacer {
  flex: 1;
}

/* Identity tile */
.left-rail__identity {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.12s ease;
  flex-shrink: 0;
  min-width: 0;
  margin-top: 4px;
  position: relative;
}

.left-rail--collapsed .left-rail__identity {
  justify-content: center;
  padding: 8px;
  width: 40px;
  height: 40px;
}

.left-rail__identity:hover {
  background: rgba(255, 255, 255, 0.06);
}

.left-rail__avatar {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 50%;
  background: #96ffc9;
  color: #181b2f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.left-rail__identity-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.left-rail__identity-name {
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Workspace switcher dropdown */
.left-rail__workspace-menu {
  background: #1a2332;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  z-index: 60;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
  margin: -8px 0 12px;
  flex-shrink: 0;
  font-family: var(--font-sans);
}

.left-rail--collapsed .left-rail__workspace-menu {
  position: absolute;
  left: 70px;
  top: 76px;
  width: 240px;
  margin: 0;
}

.left-rail__workspace-menu-header {
  padding: 10px 14px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.left-rail__workspace-menu-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.left-rail__workspace-menu-item {
  display: flex !important;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 14px !important;
  background: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  text-align: left;
  transition: background 0.12s ease;
  position: relative;
}
.left-rail__workspace-menu-item:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06) !important;
}
.left-rail__workspace-menu-item:disabled {
  cursor: default;
  opacity: 1;
}
.left-rail__workspace-menu-item--active {
  background: rgba(150, 255, 201, 0.06) !important;
  color: #ffffff;
}

.left-rail__workspace-menu-kind {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  width: 44px;
  flex-shrink: 0;
}

.left-rail__workspace-menu-name {
  font-weight: 600;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.left-rail__workspace-menu-active-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #96ffc9;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(150, 255, 201, 0.2);
}

.left-rail__workspace-menu-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  text-decoration: none;
  color: #96ffc9;
  font-size: 12px;
  font-weight: 700;
  transition: background 0.12s ease;
}
.left-rail__workspace-menu-footer:hover {
  background: rgba(255, 255, 255, 0.05);
}

/* Identity dropdown */
.left-rail__identity-menu {
  position: absolute;
  bottom: 72px;
  left: 12px;
  right: 12px;
  background: #1a2332;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  z-index: 200;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
}

.left-rail--collapsed .left-rail__identity-menu {
  left: 4px;
  right: 4px;
  bottom: 72px;
}

.left-rail__identity-menu-item {
  display: block;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.85);
  text-align: left;
  transition: background 0.12s ease;
}

.left-rail__identity-menu-item:hover {
  background: rgba(255, 255, 255, 0.06);
}

.left-rail__identity-menu-item--danger {
  color: #fa9696;
}

/* Collapse button */
.left-rail__collapse-btn {
  position: absolute;
  bottom: 16px;
  right: -14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #2c3e50;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.78);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
  z-index: 10;
  font-family: var(--font-sans);
  padding: 0;
}

.left-rail__collapse-btn:hover {
  background: #3a4f64;
  color: #ffffff;
  border-color: rgba(150, 255, 201, 0.4);
}

.left-rail__collapse-icon {
  transition: transform 0.22s ease;
  display: block;
}

.left-rail__collapse-icon--collapsed {
  transform: rotate(180deg);
}

.left-rail__beta-tag {
  display: inline-block;
  padding: 1px 6px;
  margin-right: 6px;
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
  border-radius: 4px;
  font-size: 0.65em;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  vertical-align: middle;
}
</style>
